'use client';

import { useEffect, useRef } from 'react';
import { animate, svg, stagger } from 'animejs';

interface UltragoonLoaderProps {
  size?: number;
  color?: string;
}

export default function UltragoonLoader({ size = 120, color = '#a78bfa' }: UltragoonLoaderProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const animRef = useRef<any>(null);

  useEffect(() => {
    if (!svgRef.current) return;

    const paths = svgRef.current.querySelectorAll('path');
    if (paths.length === 0) return;

    const drawables = svg.createDrawable(paths);

    animRef.current = animate(drawables, {
      draw: ['0 0', '0 1', '1 1'],
      ease: 'inOutQuad',
      duration: 1800,
      delay: stagger(80),
      loop: true,
    });

    return () => {
      if (animRef.current) animRef.current.pause();
    };
  }, []);

  return (
    <div className="w-full h-full flex items-center justify-center">
      <svg
        ref={svgRef}
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 264.02 58.27"
        width={size}
        style={{ overflow: 'visible' }}
      >
        <g>
          <path fill="none" stroke={color} strokeWidth="1.2" d="M152.03,12.09h6.84l.3.3v32.9c0,.56-.57,2.35-.79,2.98-3.49,9.82-16.77,12.76-25.08,7.24-2.4-1.59-4.97-4.66-5.45-7.54h8.13s.14.49.35.74c4.32,5.19,15.51,2.26,15.51-4.8v-3.87c-.78.18-1.27,1.09-1.89,1.58-8.65,6.9-20.56.32-22.6-9.7-1.47-7.22.95-14.51,7.26-18.52,5.38-3.42,12.76-3.32,17.13,1.66.51.01.23-2.52.3-2.97ZM142.07,18.29c-10.12,1.37-10.32,17.94-.05,18.98,13.35,1.35,13.19-20.76.05-18.98Z"/>
          <path fill="none" stroke={color} strokeWidth="1.2" d="M115.96,44l-.2-3.96c-7.5,8.34-19.93,4.38-23.88-5.35-2.51-6.18-1.5-13.92,3.19-18.81s12.49-6.59,18.18-2.76c.32.22,2.35,2.13,2.51,1.95.13-.94-.32-2.23.3-2.97h6.74v31.91h-6.84ZM105.4,18.29c-2.29.31-4.73,2.27-5.9,4.2-4.43,7.31,1.69,17.29,10.27,14.63,9.78-3.03,7.63-20.46-4.36-18.83Z"/>
          <path fill="none" stroke={color} strokeWidth="1.2" d="M191.01,39.97c-10.11,9.93-27.04,3.07-27.88-10.93-.96-16,18.24-24.29,28.84-12.39,5.64,6.33,5.04,17.43-.96,23.32ZM178.55,18.29c-11.31,1.15-10.13,19.81,1.52,19.17,11.49-.63,10.79-20.42-1.52-19.17Z"/>
          <path fill="none" stroke={color} strokeWidth="1.2" d="M202.7,16.18c12.74-12.93,32.87.34,27.23,17.42-2.7,8.17-12.17,12.92-20.38,10.26-11.43-3.71-15.26-19.14-6.85-27.68ZM208.63,20.53c-8.37,7.97,1.84,22.39,11.17,15.04s-1.81-23.95-11.17-15.04Z"/>
          <path fill="none" stroke={color} strokeWidth="1.2" d="M264.02,44h-7.53v-20.12c0-3.51-4-5.94-7.23-5.65-2.97.27-7.04,3.22-7.04,6.44v19.33h-7.53V12.09h7.33v3.17l3.83-2.81c7.2-4.27,18.17,1.66,18.17,10.05v21.51Z"/>
          <path fill="none" stroke={color} strokeWidth="1.2" d="M7.33,12.09v18.33c0,.25.22,1.55.29,1.89,1.55,7.6,13.23,6.53,13.98-1.7.41-4.47.31-10.14.21-14.67-.03-1.26-.52-2.57,0-3.86h7.33v31.91h-6.84l-.3-.3v-3.47l-2.91,2.34c-6.77,4.42-15.56,1.16-18.15-6.35-.24-.69-.93-3.03-.93-3.62V12.09h7.33Z"/>
          <path fill="none" stroke={color} strokeWidth="1.2" d="M51.93,5.45l6.73-2.98.8-.09c-.05,2.08.07,4.17,0,6.25-.03.99-.62,2.39.09,3.26h7.73c.28.17.27.4.31.68.22,1.88-.49,4.11-.21,6.06h-7.93c-.04,4.06.05,8.13,0,12.2,0,.64-.23,1.24-.22,1.98.1,4.97,4.45,4.64,8.14,4.06v6.94c-5.14,1-11.21.55-14.03-4.5-.97-1.73-1.31-3.75-1.42-5.71-.29-4.89.22-10.05,0-14.96h-5.55c-.04-1.75.06-3.51,0-5.26,0-.31-.41-1.48.09-1.48h5.45v-6.44Z"/>
          <path fill="none" stroke={color} strokeWidth="1.2" d="M41.82,0v35.38c0,.06.49.89.59,1,.88.94,2.96.91,4.17.89v6.74c-.71.15-1.35.23-2.09.21-12.04-.3-9.95-7.76-9.99-16.96,0-.99,0-1.97,0-2.96-.02-8.1,0-16.2,0-24.29h7.33Z"/>
          <path fill="none" stroke={color} strokeWidth="1.2" d="M89.99,11.1v7.23c-.55.55-1.49.22-2.26.32-2.65.35-5.62,1.97-6.96,4.33-.32.56-1.29,2.77-1.29,3.27v17.44l-.3.3h-7.23V12.09c1.35-.04,2.72.05,4.07,0,.99-.03,2.39-.62,3.26.09v4.06c2.62-3.33,6.37-5.39,10.7-5.15Z"/>
        </g>
      </svg>
    </div>
  );
}
